import { useState, useEffect } from "react";
import { motion, useScroll } from "framer-motion";
import { Sparkles } from "lucide-react";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import { ReviewStrip } from "@/components/landing/ReviewStrip";
import Portfolio from "@/components/landing/Portfolio";
import Testimonials from "@/components/landing/Testimonials";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import QuoteWizard from "@/components/landing/QuoteWizard";
import QABot from "@/components/landing/QABot";

// Global event bus so any button can open the wizard
const QUOTE_EVENT = "open-quote-wizard";
export const openQuoteWizard = () => {
  window.dispatchEvent(new CustomEvent(QUOTE_EVENT));
};

const LandingPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [wizardOpen, setWizardOpen] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    setIsLoaded(true);
    if (window.location.hash.length > 1) {
      const id = window.location.hash;
      setTimeout(() => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" }), 600);
    }
    const handleMouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handleMouseMove);
    const openHandler = () => setWizardOpen(true);
    window.addEventListener(QUOTE_EVENT, openHandler);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener(QUOTE_EVENT, openHandler);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] overflow-x-clip relative">
      {/* Cursor glow */}
      <motion.div
        className="fixed w-64 h-64 rounded-full pointer-events-none z-50"
        style={{
          background: "radial-gradient(circle, rgba(14, 165, 233, 0.08) 0%, transparent 70%)",
          left: mousePos.x - 128,
          top: mousePos.y - 128,
        }}
        animate={{ left: mousePos.x - 128, top: mousePos.y - 128 }}
        transition={{ type: "spring", damping: 30, stiffness: 200 }}
      />

      {/* Progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-sky-400 via-sky-500 to-sky-400 origin-left z-[100]"
        style={{ scaleX: scrollYProgress }}
      />

      <Header onQuoteClick={() => setWizardOpen(true)} />

      <main className="relative z-10">
        <Hero isLoaded={isLoaded} onQuoteClick={() => setWizardOpen(true)} />
        <ReviewStrip />
        <Portfolio />
        <Testimonials />
        <Contact />
      </main>

      <Footer />

      {/* Sticky floating quote CTA */}
      <motion.button
        data-testid="floating-quote-cta"
        onClick={() => setWizardOpen(true)}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="float-cta fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-5 py-3 rounded-full bg-sky-500 hover:bg-sky-400 text-white font-medium shadow-[0_10px_40px_rgba(14,165,233,0.45)] hidden sm:flex"
      >
        <Sparkles className="w-4 h-4" />
        AI Quote
      </motion.button>

      <QuoteWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      <QABot />
    </div>
  );
};

export default LandingPage;
